import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Truck, User, Phone, MapPin, Clock, Package, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { formatVehicleType } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LogisticsInfoCard = ({ operationId, operation }) => {
  const [logistics, setLogistics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLogistics();
  }, [operationId]);

  const fetchLogistics = async () => {
    try {
      const response = await axios.get(`${API}/operations/${operationId}/logistics`);
      if (response.data && response.data.length > 0) {
        // Get the most recent logistics entry
        setLogistics(response.data[0]);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching logistics:', error);
      setLoading(false);
    }
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return 'N/A';
    try {
      return new Date(datetime).toLocaleString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return datetime;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-gray-500 text-center">Chargement des informations...</p>
        </CardContent>
      </Card>
    );
  }

  // If no logistics info yet
  if (!logistics) {
    return (
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-blue-900">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm">
              Les informations logistiques détaillées seront disponibles une fois l'opération planifiée.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with badges */}
      <div className="flex items-center gap-2 flex-wrap">
        {operation?.intraInterLand && (
          <Badge 
            className={
              operation.intraInterLand === 'INTRALAND' 
                ? 'bg-green-100 text-green-800 border-green-200' 
                : 'bg-orange-100 text-orange-800 border-orange-200'
            }
          >
            {operation.intraInterLand}
          </Badge>
        )}
        
        {operation?.assignedOperatorName && (
          <Badge className="bg-purple-100 text-purple-800 border-purple-200">
            <User className="w-3 h-3 mr-1" />
            Opérateur : {operation.assignedOperatorName}
          </Badge>
        )}
      </div>

      {/* Vehicle Information */}
      {(logistics.vehiclePlate || logistics.vehicleType) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Truck className="w-5 h-5 text-[#1db584]" />
              Véhicule Assigné
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {logistics.vehicleType && (
              <div>
                <p className="text-sm text-gray-500">Type de véhicule</p>
                <p className="font-medium">{formatVehicleType(logistics.vehicleType)}</p>
              </div>
            )}
            
            {logistics.vehiclePlate && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-gray-500">Plaque d'immatriculation</p>
                  <p className="font-medium text-lg">{logistics.vehiclePlate}</p>
                </div>
              </>
            )}
            
            {logistics.vehicleBrand && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-gray-500">Marque</p>
                  <p className="font-medium">{logistics.vehicleBrand} {logistics.vehicleModel || ''}</p>
                </div>
              </>
            )}
            
            {logistics.hasTrailer && logistics.trailerPlate && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-gray-500">Remorque</p>
                  <p className="font-medium">{logistics.trailerPlate}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Driver Information */}
      {(logistics.driverName || (logistics.assignments && logistics.assignments.length > 0)) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-[#1db584]" />
              Chauffeur
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Nom et Prénom</p>
              <p className="font-medium">{logistics.driverName || logistics.assignments?.[0]?.driverName}</p>
            </div>
            
            {/* Contact Opérationnel — affiche le numéro opérateur/field ops, jamais le numéro direct du chauffeur */}
            {(() => {
              // Priorité 1 : contactPhone explicite dans l'assignation
              const firstAssignmentContact = logistics.assignments?.[0]?.contactPhone;
              const contactPhoneLegacy = logistics.contactPhone;
              
              // Priorité 2 : numéro opérateur et/ou field ops depuis l'opération
              const operatorPhone = operation?.assignedOperatorPhone;
              const fieldOpsPhone = operation?.assignedFieldOpsPhone;
              
              // Numéro de secours Baobab Connect
              const FALLBACK_BAOBAB = '+225 05 64 73 09 09';

              const contacts = [];
              if (firstAssignmentContact || contactPhoneLegacy) {
                contacts.push(firstAssignmentContact || contactPhoneLegacy);
              } else {
                if (operatorPhone) contacts.push(operatorPhone);
                if (fieldOpsPhone && fieldOpsPhone !== operatorPhone) contacts.push(fieldOpsPhone);
              }
              if (contacts.length === 0) contacts.push(FALLBACK_BAOBAB);

              return (
                <>
                  <Separator />
                  <div>
                    <p className="text-sm text-gray-500">Contact Opérationnel</p>
                    <div className="space-y-1 mt-1">
                      {contacts.map((phone, idx) => (
                        <a
                          key={idx}
                          href={`tel:${phone}`}
                          className="font-medium text-[#1db584] hover:underline flex items-center gap-1"
                        >
                          <Phone className="w-4 h-4" />
                          {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Operational Timeline */}
      {(logistics.loadingStartTime || logistics.departureTime || logistics.deliveryTime) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-[#1db584]" />
              Suivi Opérationnel
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {logistics.loadingStartTime && (
              <div>
                <p className="text-sm text-gray-500">Début du chargement</p>
                <p className="font-medium">{formatDateTime(logistics.loadingStartTime)}</p>
                {logistics.loadingLocation && (
                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    {logistics.loadingLocation}
                  </p>
                )}
              </div>
            )}
            
            {logistics.departureTime && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-gray-500">Départ en route</p>
                  <p className="font-medium">{formatDateTime(logistics.departureTime)}</p>
                </div>
              </>
            )}
            
            {logistics.deliveryTime && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-gray-500">Livraison effectuée</p>
                  <p className="font-medium">{formatDateTime(logistics.deliveryTime)}</p>
                  {logistics.recipientName && (
                    <p className="text-sm text-gray-600 mt-1">
                      Reçu par : {logistics.recipientName}
                      {logistics.recipientFunction && ` (${logistics.recipientFunction})`}
                    </p>
                  )}
                </div>
              </>
            )}
            
            {logistics.hasReservation && (
              <>
                <Separator />
                <div className="p-3 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm font-medium text-orange-900 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Réserves émises
                  </p>
                  {logistics.reservationDetails && (
                    <p className="text-sm text-orange-800 mt-1">{logistics.reservationDetails}</p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment Information (if available) */}
      {logistics.invoiceAmount && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Package className="w-5 h-5 text-[#1db584]" />
              Informations de Facturation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Montant facturé</p>
              <p className="font-medium text-xl">
                {logistics.invoiceAmount?.toLocaleString('fr-FR')} {logistics.currency || 'XOF'}
              </p>
            </div>
            
            {logistics.paymentDueDate && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-gray-500">Date d'échéance</p>
                  <p className="font-medium">{formatDateTime(logistics.paymentDueDate)}</p>
                </div>
              </>
            )}
            
            {logistics.paymentMode && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-gray-500">Mode de paiement</p>
                  <p className="font-medium capitalize">{logistics.paymentMode.replace('_', ' ')}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LogisticsInfoCard;
